export type DispatchItem = {
  id: string;
  scheduledStart: string;
  scheduledEnd: string;
  status: string;
	jobId: string | null;
	ownerType?: 'job' | 'estimate' | 'membership';
	ownerId?: string;
  jobNumber: string | number | null;
  description: string | null;
  priority: string | null;
  customerId: string;
  customerName: string;
  address: string;
  technicianIds: string[];
  technicianNames: string | null;
  conflicts: string[];
  requiredSkillIds?: string[];
  requiredSkills?: string[];
  requiredSkillsSource?: "job_type_default" | "manual_override" | null;
  jobTypeName?: string | null;
  membershipStatus?: string | null;
  customerConfirmedAt?: string | null;
  serviceZoneNames?: string[];
  serviceZones?: Array<{ id: string; name: string; color: string }>;
  postalCode?: string | null;
  locationTags?: Array<{ id: string; name: string; color: string | null }>;
  dispatchedAt?: string | null;
  enRouteAt?: string | null;
  arrivedAt?: string | null;
  departedAt?: string | null;
  technicianProgress?: DispatchProgress[];
};

export type DispatchProgress = {
  technicianId: string;
  status: string;
  dispatchedAt: string | null;
  enRouteAt: string | null;
  arrivedAt: string | null;
  completedAt: string | null;
};

/** A crew visit has independent progress on each technician's board row. */
export function dispatchItemForTechnician(item: DispatchItem, technicianId?: string): DispatchItem {
  const progress = technicianId ? item.technicianProgress?.find(entry => entry.technicianId === technicianId) : undefined;
  if (!progress) return item;
  return { ...item, status: progress.status, dispatchedAt: progress.dispatchedAt, enRouteAt: progress.enRouteAt,
    arrivedAt: progress.arrivedAt, departedAt: progress.completedAt };
}

/** Actual elapsed time, clipped to the displayed company-local board window. */
export function dispatchElapsedSegments(item: DispatchItem, technicianId: string, boardDate: string, timezone: string, startHour: number, endHour: number, now: number) {
  const progress = item.technicianProgress?.find(entry => entry.technicianId === technicianId);
  const state = progress?.status ?? item.status;
  if (['pending', 'scheduled', 'cancelled', 'canceled', 'no_show'].includes(state)) return [];
  const dispatch = progress ? progress.dispatchedAt ?? progress.enRouteAt : item.dispatchedAt ?? item.enRouteAt;
  const arrival = progress ? progress.arrivedAt : item.arrivedAt;
  const departure = progress ? progress.completedAt : item.departedAt;
  const active = ['dispatched', 'en_route', 'arrived', 'in_progress'].includes(state);
  const windowStart = Date.parse(companyLocalToIso(`${boardDate}T00:00`, timezone));
  const nextDate = new Date(`${boardDate}T12:00:00Z`);
  nextDate.setUTCDate(nextDate.getUTCDate() + 1);
  const dayEnd = Date.parse(companyLocalToIso(`${nextDate.toISOString().slice(0, 10)}T00:00`, timezone));
  const axisPosition = (time: number) => {
    if (time <= windowStart) return 0;
    if (time >= dayEnd) return 100;
    const local = isoToCompanyLocal(new Date(time).toISOString(), timezone);
    const hour = Number(local.slice(11, 13)) + Number(local.slice(14, 16)) / 60;
    return Math.max(0, Math.min(100, ((hour - startHour) / (endHour - startHour)) * 100));
  };
  const intervals = [
    {kind: 'dispatch' as const, start: dispatch, end: arrival ?? departure ?? (active ? new Date(now).toISOString() : null)},
    {kind: 'arrived' as const, start: arrival, end: departure ?? (active ? new Date(now).toISOString() : null)},
  ];
  return intervals.flatMap(interval => {
    const start = Date.parse(interval.start ?? ''), end = Math.min(now, Date.parse(interval.end ?? ''));
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start || end < windowStart || start >= dayEnd) return [];
    const left = axisPosition(start), right = axisPosition(end);
    if (right <= left) return [];
    const minutes = Math.floor((end - start) / 60000);
    const duration = minutes >= 60 ? `${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ''}` : `${minutes}m`;
    return [{kind: interval.kind, left, width: right - left, label: `${interval.kind === 'dispatch' ? 'Dispatched' : 'Arrived'} ${duration}`}];
  });
}

export type Technician = {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  photoAttachmentId?: string | null;
  active?: boolean;
  dispatchable?: boolean;
  skills?: string[];
  skillIds?: string[];
};

export function rankTechniciansForSkills(technicians: Technician[], requiredSkillIds: string[]) {
  const required = new Set(requiredSkillIds);
  return technicians.map((technician) => {
    const matched = (technician.skillIds || []).filter((id) => required.has(id)).length;
    return { technician, matched, required: required.size, rank: required.size === 0 ? 0 : matched === required.size ? 0 : matched > 0 ? 1 : 2 };
  }).sort((left, right) => left.rank - right.rank || right.matched - left.matched || left.technician.name.localeCompare(right.technician.name));
}

export function techniciansMatchingSkills(technicians: Technician[], requiredSkillIds: string[] | undefined) {
  if (!Array.isArray(requiredSkillIds) || requiredSkillIds.length === 0) return technicians;
  return technicians.filter((technician) => requiredSkillIds.every((id) => (technician.skillIds || []).includes(id)));
}

export type ScheduleBlock = {
  id: string;
  technicianId: string;
  startsAt: string;
  endsAt: string;
  kind: string;
  title: string;
  allDay?: boolean;
  paid?: boolean;
  note?: string | null;
};

export type DispatchWeekDay = {
  date: string;
  total: number;
  unassigned: number;
  pending: number;
  conflicts: number;
  workingTechnicians: number;
};

export type DispatchWeek = {
  weekStart: string;
  weekEnd: string;
  timezone: string;
  days: DispatchWeekDay[];
};

export type DispatchProposal = {
  id: string;
  changeType: "create_appointment" | "move_appointment" | "reassign_appointment";
  subjectId: string | null;
  status: "pending" | "approved" | "rejected" | "applied" | "cancelled";
  requestedByName: string | null;
  reviewedByName: string | null;
  appliedByName: string | null;
  reviewedAt: string | null;
  appliedAt: string | null;
  createdAt: string;
  payload: {
    version: 1;
    action: Record<string, unknown> & { kind: string };
    display: {
      jobId: string | null;
      jobNumber: string | number | null;
      ownerType?: 'job' | 'estimate' | 'membership';
      ownerId?: string;
      customerId: string;
      customerName: string;
      address: string;
      technicianNames: string[];
      oldStart: string | null;
      oldEnd: string | null;
      newStart: string;
      newEnd: string;
      conflicts: string[];
    };
  };
};

export type OperatorNavigation = {
  view: "dispatch" | "customers" | "customer" | "job";
  date?: string;
  customerId?: string;
  jobId?: string;
};

function zonedParts(value: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(value);
  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
}

export function companyLocalToIso(value: string, timezone: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) throw new Error("Use a valid date and time");
  const desired = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]));
  let guess = desired;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const actual = zonedParts(new Date(guess), timezone);
    const represented = Date.UTC(Number(actual.year), Number(actual.month) - 1, Number(actual.day), Number(actual.hour), Number(actual.minute));
    guess += desired - represented;
  }
  return new Date(guess).toISOString();
}

export function isoToCompanyLocal(value: string, timezone: string) {
  const parts = zonedParts(new Date(value), timezone);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

export function formatDispatchTime(value: string, timezone: string) {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

export function formatDispatchDate(value: string, timezone: string, options?: Intl.DateTimeFormatOptions) {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    ...options,
  }).format(new Date(value));
}

const BOOKABLE_BLOCK_KINDS = new Set(["working", "on_call"]);

export type OpenWindow = { startsAt: string; endsAt: string };

export function technicianOpenWindows(blocks: ScheduleBlock[], appointments: DispatchItem[], technicianId: string, minimumMinutes = 30): OpenWindow[] {
  type Range = { start: number; end: number };
  const working: Range[] = blocks
    .filter((block) => block.technicianId === technicianId && BOOKABLE_BLOCK_KINDS.has(block.kind))
    .map((block) => ({ start: Date.parse(block.startsAt), end: Date.parse(block.endsAt) }))
    .filter((range) => range.end > range.start)
    .sort((a, b) => a.start - b.start);
  const booked: Range[] = appointments
    .filter((item) => item.technicianIds.includes(technicianId))
    .map((item) => ({ start: Date.parse(item.scheduledStart), end: Date.parse(item.scheduledEnd) }))
    .sort((a, b) => a.start - b.start);
  const unavailable: Range[] = blocks
    .filter((block) => block.technicianId === technicianId && !BOOKABLE_BLOCK_KINDS.has(block.kind))
    .map((block) => ({ start: Date.parse(block.startsAt), end: Date.parse(block.endsAt) }))
    .filter((range) => range.end > range.start)
    .sort((a, b) => a.start - b.start);
  const open: Range[] = [];
  for (const shift of working) {
    let cursor = shift.start;
    for (const job of booked) {
      if (job.end <= cursor || job.start >= shift.end) continue;
      if (job.start > cursor) open.push({ start: cursor, end: Math.min(job.start, shift.end) });
      cursor = Math.max(cursor, job.end);
    }
    if (cursor < shift.end) open.push({ start: cursor, end: shift.end });
  }
  const available = open.flatMap((window) => {
    let segments: Range[] = [window];
    for (const blocked of unavailable) {
      segments = segments.flatMap((segment) => {
        if (blocked.end <= segment.start || blocked.start >= segment.end) return [segment];
        return [
          ...(blocked.start > segment.start ? [{ start: segment.start, end: Math.min(blocked.start, segment.end) }] : []),
          ...(blocked.end < segment.end ? [{ start: Math.max(blocked.end, segment.start), end: segment.end }] : []),
        ];
      });
    }
    return segments;
  });
  return available
    .filter((window) => window.end - window.start >= minimumMinutes * 60_000)
    .map((window) => ({ startsAt: new Date(window.start).toISOString(), endsAt: new Date(window.end).toISOString() }));
}

export function snapLaneMinutes(ratio: number, startHour: number, span: number, stepMinutes = 15) {
  const safeRatio = Number.isFinite(ratio) ? Math.min(1, Math.max(0, ratio)) : 0;
  const raw = (startHour + safeRatio * span) * 60;
  const snapped = Math.round(raw / stepMinutes) * stepMinutes;
  return Math.min(24 * 60 - stepMinutes, Math.max(0, snapped));
}

/**
 * Late is derived, not sent. Nothing in the payload says a job is behind, so
 * the board says it: the window opened today, and the technician has not set
 * off, arrived, started, finished or been cancelled.
 */
const SETTLED = new Set(["en_route", "arrived", "in_progress", "completed", "cancelled", "no_show"]);
/**
 * A job that has been done, cancelled or is under way is no longer something
 * the office can act on. It cannot be "nobody assigned" or "double booked" in
 * any useful sense — that is history, not a hole in the day (Melania, 1/9/2026).
 */
export function isSettled(item: { status: string }) {
  return SETTLED.has(item.status.toLowerCase());
}
/**
 * Late is a today thing. A window that opened this morning with nobody on the
 * way is something the office can still do something about. A job from ten
 * weeks ago that was never marked complete is bookkeeping, and tomorrow's job
 * cannot be late yet — neither one gets the red ring (Melania, 2/9/2026).
 */
export function isRunningLate(item: DispatchItem, timezone: string, now = Date.now()) {
  if (SETTLED.has(item.status.toLowerCase())) return false;
  const start = Date.parse(item.scheduledStart);
  if (!(start < now)) return false;
  return (
    isoToCompanyLocal(item.scheduledStart, timezone).slice(0, 10) ===
    isoToCompanyLocal(new Date(now).toISOString(), timezone).slice(0, 10)
  );
}

/**
 * The API lists every reason a visit is awkward: outside his hours, across
 * his lunch, on top of another job. Only the last one is a double booking.
 * The rest are worth a line on hover, not a red ring on every block on the
 * board (Melania, 2/9/2026: "why are they all red?").
 */
export function softConflicts(item: Pick<DispatchItem, "conflicts">) {
  return item.conflicts.filter((reason) => !/another appointment/i.test(reason));
}

export function minutesToLocalTime(minutes: number) {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function stackAppointments(items: DispatchItem[]): Array<{ item: DispatchItem; row: number }> {
  const rowEnds: number[] = [];
  return [...items]
    .sort((a, b) => Date.parse(a.scheduledStart) - Date.parse(b.scheduledStart) || Date.parse(a.scheduledEnd) - Date.parse(b.scheduledEnd))
    .map((item) => {
      const start = Date.parse(item.scheduledStart);
      const end = Date.parse(item.scheduledEnd);
      let row = rowEnds.findIndex((rowEnd) => rowEnd <= start);
      if (row === -1) row = rowEnds.length;
      rowEnds[row] = Math.max(rowEnds[row] ?? 0, end);
      return { item, row };
    });
}
